import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'light' | 'dark';
type Ctx = { theme: Theme; setTheme: (t: Theme) => void; effectiveTheme: Theme };

const ThemeContext = createContext<Ctx>({
  theme: 'light',
  setTheme: () => {},
  effectiveTheme: 'light',
});

function getSystem(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getSavedTheme(): Theme | null {
  try {
    const v = localStorage.getItem('softmaze.theme');
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initial: user choice if any, otherwise system
  const saved = getSavedTheme();
  const [theme, setTheme] = useState<Theme>(saved ?? getSystem());

  // Follow OS only if user hasn't chosen explicitly
  useEffect(() => {
    if (saved) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setTheme(mql.matches ? 'dark' : 'light');
    update();
    if (mql.addEventListener) mql.addEventListener('change', update);
    else mql.addListener(update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', update);
      else mql.removeListener(update);
    };
  }, [saved]);

  // Reflect on <html>
  useEffect(() => {
    const dd = document.documentElement;
    dd.setAttribute('data-theme', theme);
    (dd.style as any).colorScheme = theme;
  }, [theme]);

  // Persist
  useEffect(() => {
    try { localStorage.setItem('softmaze.theme', theme); } catch {}
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme, effectiveTheme: theme }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() { return useContext(ThemeContext); }